import { getCompanyProjects, getCompanyUsers, companyTakeOwnership } from '@client-shared/api/company.api.js'

const abortControllers = {}

const abortAndCreate = (key) => {
  if (abortControllers[key] instanceof AbortController) {
    abortControllers[key].abort()
  }

  abortControllers[key] = new AbortController()

  return abortControllers[key]
}

export default {
  namespaced: true,

  state: () => ({
    projects: [],
    users: [],
    markedUser: null,
    isLoading: false,
    isLoadError: false,
    selectedMultiActionProperty: null,
  }),

  mutations: {
    SET_PROJECTS (state, projects) {
      state.projects = projects
    },

    SET_USERS (state, users) {
      state.users = users
    },

    SET_IS_LOADING (state, isLoading) {
      state.isLoading = isLoading
    },

    SET_IS_LOAD_ERROR (state, isLoadError) {
      state.isLoadError = isLoadError
    },

    UPDATE_USER (state, { userIndex, user }) {
      state.users.splice(userIndex, 1, user)
    },

    UPDATE_PROJECT (state, updatedProject) {
      const index = state.projects.findIndex(p => String(p._id) === String(updatedProject._id))

      if (index !== -1) {
        state.projects.splice(index, 1, updatedProject)
      }
    },

    SET_MARKED_USER (state, user) {
      state.markedUser = user
    },

    SET_SELECTED_MULTI_ACTION_PROPERTY (state, property) {
      state.selectedMultiActionProperty = property
    },
  },

  actions: {
    async fetch ({ commit }) {
      commit('SET_IS_LOADING', true)
      commit('SET_IS_LOAD_ERROR', false)

      try {
        const controller = abortAndCreate('fetch')

        const [
          projects,
          users,
        ] = await Promise.all([
          getCompanyProjects({
            axios: this.$axios,
            signal: controller.signal,
          }),
          getCompanyUsers({
            axios: this.$axios,
            signal: controller.signal,
          }),
        ])

        commit('SET_PROJECTS', projects)
        commit('SET_USERS', users)
      } catch (error) {
        if (this.$axios.isCancel(error)) {
          return
        }

        commit('SET_IS_LOAD_ERROR', true)

        throw error
      } finally {
        commit('SET_IS_LOADING', false)
      }
    },

    async fetchUsers ({ commit }) {
      commit('SET_IS_LOADING', true)
      commit('SET_IS_LOAD_ERROR', false)

      try {
        const controller = abortAndCreate('fetchUsers')

        const users = await getCompanyUsers({
          axios: this.$axios,
          signal: controller.signal,
        })

        commit('SET_USERS', users ?? [])
      } catch (error) {
        if (this.$axios.isCancel(error)) {
          return
        }

        commit('SET_IS_LOAD_ERROR', true)

        throw error
      } finally {
        commit('SET_IS_LOADING', false)
      }
    },

    async fetchProjects ({ commit }) {
      commit('SET_IS_LOADING', true)
      commit('SET_IS_LOAD_ERROR', false)

      try {
        const controller = abortAndCreate('fetchProjects')

        const projects = await getCompanyProjects({
          axios: this.$axios,
          signal: controller.signal,
        })

        commit('SET_PROJECTS', projects)
      } catch (error) {
        if (this.$axios.isCancel(error)) {
          return
        }

        commit('SET_IS_LOAD_ERROR', true)

        throw error
      } finally {
        commit('SET_IS_LOADING', false)
      }
    },

    async takeOwnership ({ commit, state }, { project, signal }) {
      const updatedProject = await companyTakeOwnership({
        axios: this.$axios,
        project,
        signal,
      })

      const oldProject = state.projects.find(p => String(p._id) === String(project._id))
      const formerOwnerUserId = oldProject?.computed?.ownerParticipant?.user?._id

      commit('UPDATE_PROJECT', updatedProject)

      if (formerOwnerUserId) {
        const stillOwnsProject = state.projects.some(p =>
          String(p.computed?.ownerParticipant?.user?._id) === String(formerOwnerUserId),
        )

        if (!stillOwnsProject) {
          const userIndex = state.users.findIndex(u => String(u._id) === String(formerOwnerUserId))

          if (userIndex !== -1) {
            commit('UPDATE_USER', {
              userIndex,
              user: {
                ...state.users[userIndex],
                isProjectOwner: false,
              },
            })
          }
        }
      }
    },
  },
}
